import {
  buildContextualTemplateDictionaryOptions,
  getTemplateDefaultOptions,
} from "@/components/features/matter/file-repo/GenerateDocument/buildContextualTemplateDictionaryOptions";
import { getAutoAppliedContactTemplateFieldValue } from "@/components/features/matter/file-repo/GenerateDocument/contactTemplateValues";
import type { MatterDocumentSubItem } from "@/lib/types";
import {
  buildSettlementNegotiationContactConnections,
  getSettlementNegotiationContactIds,
  isSettlementNegotiationContactConnection,
} from "@/components/features/matter/file-repo/GenerateDocument/settlementNegotiationTemplateContacts";
import { CreatableSelect } from "@/components/features/matter/file-repo/GenerateDocument/CreatableSelect";
import type { PreviewVariableConfig } from "@/components/features/matter/file-repo/GenerateDocument/BrowserDocxTemplatePreview";
import {
  buildTemplateDictionaryOptions,
  buildInsuranceWithConnections,
  buildIncidentReportSourceConnections,
  buildMedicalTreatmentProviderConnections,
  getMedicalTreatmentProviderContactIds,
  type ContactConnectionWithContact,
  type InsuranceWithConnections,
  type TemplateDictionaryOptionsMap,
} from "@/components/features/matter/file-repo/GenerateDocument/templateDictionaryOptions";
import {
  Input,
  LoadingSpinner,
  Separator,
  Textarea,
  Typography,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useVehiclesViewModel } from "@/hooks/matter-view-models/use-vehicles-view-model";
import { useFormatInsurancePolicyLimits } from "@/hooks/useFormatInsurancePolicyLimits";
import {
  getFirmOfficesQueryOptions,
  templateContactsQueryOptions,
} from "@/lib/data/react-query";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { templateDictionaryItems } from "@/lib/data/static/templateDictionaryItems";
import { getInsuranceContactConnections } from "@/lib/utils/connections/insurance-contact-connections";
import { getEmptyMatterForFirmTemplateMapping } from "@/lib/utils/matters/emptyMatterForTemplateMapping";
import {
  getMatterContactConnections,
  getThirdPartyMatterContactIds,
} from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { groupBy, sortBy, startCase, uniq } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import {
  buildSignatureRequestMergeInputs,
  getBaseKeyForSignatureRequestVariable,
} from "../signatureRequestMergeInputs";
import type { SignatureRequestSigner } from "../signatureRequestSigners";
import { InteractiveSignatureRequestDocxPreview } from "./InteractiveSignatureRequestDocxPreview";
import {
  buildInstanceGroups,
  getGroupHeadingSuffix,
  getVisibleGroupItems,
  type TemplateDictionaryFormItem,
} from "./mapVariableGrouping";
import {
  SignerSlotAssignments,
  useSignerSlotAssignments,
} from "./SignerSlotAssignments";
import type { SignatureRequestPreviewDocument } from "./SignatureRequestDocumentPreview";

interface MapVariablesStepProps {
  subItem?: MatterDocumentSubItem;
  matterId: string;
  /** Global wizard: user chose the Firm tab (no matter retrieve); map variables using firm profile + typed values. */
  isFirmSignatureFlow?: boolean;
  selectedDocument: SignatureRequestPreviewDocument | null;
  selectedDocumentVariables: string[];
  mappedVariableValues: Record<string, string>;
  setMappedVariableValues: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  signers: SignatureRequestSigner[];
  setSigners: React.Dispatch<React.SetStateAction<SignatureRequestSigner[]>>;
}

type ParsedVariable = {
  baseVariable: string;
  alias?: string;
  format?: string;
  instance?: number;
};

const FORMAT_TOKENS = new Set([
  "upperCase",
  "lowerCase",
  "titleCase",
  "plural",
  "possessive",
  "short",
  "long",
]);

const CONTACT_ROLE_PREFIXES = [
  ["client", "Client"],
  ["defendant", "Defendant"],
  ["witness", "Witness"],
  ["provider", "Provider"],
  ["primaryContact", "Primary Contact"],
  ["incidentReportSource", "Incident Report Source"],
  ["bodilyInjuryAdjuster", "Bodily Injury Adjuster"],
  ["propertyDamageAdjuster", "Property Damage Adjuster"],
  ["pdAdjuster", "Property Damage Adjuster"],
  ["medPayAdjuster", "MedPay Adjuster"],
  ["adjusterSupervisor", "Adjuster Supervisor"],
  ["adjuster", "Adjuster"],
] as const;

const normalizeBaseVariable = (baseVar: string) => {
  const match = CONTACT_ROLE_PREFIXES.find(([prefix]) =>
    baseVar.startsWith(prefix),
  );
  if (!match) {
    return { normalized: baseVar, role: undefined as string | undefined };
  }
  const [prefix, role] = match;
  return {
    normalized: baseVar.replace(new RegExp(`^${prefix}`), "contact"),
    role,
  };
};

const getRoleLabelForKey = (key: string) => {
  const match = CONTACT_ROLE_PREFIXES.find(([prefix]) => prefix === key);
  return match?.[1];
};

const parseVariable = (variable: string): ParsedVariable => {
  const [base, ...rest] = variable.split("_");
  let format: string | undefined;
  let instance: number | undefined;
  const aliasParts: string[] = [];

  rest.forEach((part) => {
    if (!Number.isNaN(Number(part))) {
      if (instance === undefined) {
        instance = Number(part);
      }
      return;
    }
    if (!format && FORMAT_TOKENS.has(part)) {
      format = part;
      return;
    }
    aliasParts.push(part);
  });

  return {
    baseVariable: base,
    alias: aliasParts.length > 0 ? aliasParts.join("_") : undefined,
    format,
    instance,
  };
};

const getBaseKeyForVariable = (variable: string): string => {
  const parsed = parseVariable(variable);
  const aliasPart = parsed.alias ? `_${parsed.alias}` : "";
  const instancePart = parsed.instance ? `_${parsed.instance}` : "";
  return `${parsed.baseVariable}${aliasPart}${instancePart}`;
};

const resolveTemplateDictionaryOptionsForItem = (
  dictionaryItem: TemplateDictionaryFormItem,
  templateDictionaryOptions: TemplateDictionaryOptionsMap,
  parentKey: string,
) => {
  const { baseVariable } = dictionaryItem;
  let opts = templateDictionaryOptions[baseVariable];
  let usedContactPool = false;

  if (!opts) {
    const { normalized } = normalizeBaseVariable(baseVariable);
    let normalizedKey = normalized;
    if (normalizedKey === baseVariable) {
      normalizedKey = baseVariable.replace(/^insurance/, "contact");
    }

    if (normalizedKey !== baseVariable) {
      opts = templateDictionaryOptions[normalizedKey] ?? [];
      usedContactPool = normalizedKey.startsWith("contact");
    } else {
      opts = [];
    }
  }

  if (usedContactPool) {
    const role =
      parentKey === "insurance" ? "Insurer" : getRoleLabelForKey(parentKey);
    if (role) {
      return opts.filter(
        (option) =>
          typeof option.context === "string" &&
          (option.context === role || option.context.includes(` - ${role}`)),
      );
    }
  }

  return opts;
};

const getGeneratedLockedVariableValues = (
  selectedDocumentVariables: string[],
  templateDictionaryOptions: TemplateDictionaryOptionsMap,
) =>
  selectedDocumentVariables.reduce<Record<string, string>>((acc, variable) => {
    if (variable.startsWith("Signer") || variable.startsWith("UserSigner")) {
      return acc;
    }

    const baseVariable = variable.split("_")[0];
    const dictionaryItem = templateDictionaryItems.find(
      (item) => item.variable === baseVariable,
    );

    if (!dictionaryItem?.lockedAtGeneration) {
      return acc;
    }

    const options = resolveTemplateDictionaryOptionsForItem(
      {
        ...dictionaryItem,
        baseVariable,
        baseKey: variable,
        isFormattingVariant: variable !== baseVariable,
        displayTitle: dictionaryItem.title,
      },
      templateDictionaryOptions,
      dictionaryItem.parent,
    );

    const generatedValue = options[0]?.value;
    if (!generatedValue) {
      return acc;
    }

    acc[variable] = generatedValue;
    return acc;
  }, {});

type MapVariablesFormProps = {
  matterId: string;
  selectedDocument: SignatureRequestPreviewDocument | null;
  selectedDocumentVariables: string[];
  templateDictionaryOptions: TemplateDictionaryOptionsMap;
  defaultTemplateDictionaryOptions?: TemplateDictionaryOptionsMap;
  autoApplyContacts?: ContactConnectionWithContact[];
  mappedVariableValues: Record<string, string>;
  setMappedVariableValues: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  canEditDocumentFiles: boolean;
  /** Shown under the step title when mapping without a loaded matter (global Firm tab). */
  firmFlowHelperText?: string;
  signers: SignatureRequestSigner[];
  setSigners: React.Dispatch<React.SetStateAction<SignatureRequestSigner[]>>;
};

function MapVariablesForm({
  matterId,
  selectedDocument,
  selectedDocumentVariables,
  templateDictionaryOptions,
  defaultTemplateDictionaryOptions = templateDictionaryOptions,
  autoApplyContacts,
  mappedVariableValues,
  setMappedVariableValues,
  canEditDocumentFiles,
  firmFlowHelperText,
  signers,
  setSigners,
}: MapVariablesFormProps) {
  const mappableVariables = useMemo(
    () =>
      selectedDocumentVariables.filter(
        (variable) =>
          !variable.startsWith("Signer") && !variable.startsWith("UserSigner"),
      ),
    [selectedDocumentVariables],
  );
  const signerAssignments = useSignerSlotAssignments({
    selectedDocumentVariables,
    setSigners,
    signers,
  });
  const generatedLockedVariableValues = useMemo(
    () =>
      getGeneratedLockedVariableValues(
        selectedDocumentVariables,
        templateDictionaryOptions,
      ),
    [selectedDocumentVariables, templateDictionaryOptions],
  );
  const effectiveMappedVariableValues = useMemo(
    () => ({
      ...mappedVariableValues,
      ...generatedLockedVariableValues,
    }),
    [generatedLockedVariableValues, mappedVariableValues],
  );

  useEffect(() => {
    if (Object.keys(generatedLockedVariableValues).length === 0) {
      return;
    }

    setMappedVariableValues((prev) => {
      const next = { ...prev };
      let hasChanges = false;

      for (const [key, value] of Object.entries(
        generatedLockedVariableValues,
      )) {
        if (next[key] !== value) {
          next[key] = value;
          hasChanges = true;
        }
      }

      return hasChanges ? next : prev;
    });
  }, [generatedLockedVariableValues, setMappedVariableValues]);

  const unknownItems: TemplateDictionaryFormItem[] = useMemo(
    () =>
      sortBy(
        mappableVariables
          .filter((item) => {
            const baseVariable = item.split("_")[0];
            return !templateDictionaryItems.some(
              (dictionaryItem) => dictionaryItem.variable === baseVariable,
            );
          })
          .map((item) => {
            const { baseVariable, instance, alias } = parseVariable(item);
            const baseKey = getBaseKeyForVariable(item);
            const suffix = alias
              ? ` (${startCase(alias)})`
              : instance
                ? ` #${instance}`
                : "";
            const displayTitle = `${startCase(baseVariable)}${suffix}`;

            return {
              title: displayTitle,
              variable: item,
              parent: "unknown" as const,
              type: "string" as const,
              baseVariable,
              baseKey,
              isFormattingVariant: item !== baseKey,
              displayTitle,
              alias,
              instance,
            };
          }),
        ["title"],
      ) as TemplateDictionaryFormItem[],
    [mappableVariables],
  );

  const templateDictionaryItemsGroupedByParent = useMemo(() => {
    const knownItems = sortBy(
      templateDictionaryItems.filter((item) =>
        mappableVariables.some(
          (templateVar) => templateVar.split("_")[0] === item.variable,
        ),
      ),
      ["title"],
    ).flatMap((item) => {
      const matchingVariables = mappableVariables.filter(
        (v) => v.split("_")[0] === item.variable,
      );

      if (matchingVariables.length === 0) {
        const baseKey = getBaseKeyForVariable(item.variable);
        return [
          {
            ...item,
            baseVariable: item.variable,
            baseKey,
            isFormattingVariant: item.variable !== baseKey,
            displayTitle: item.title,
            alias: undefined,
            instance: undefined,
          },
        ];
      }

      return matchingVariables.map((variableName) => {
        const parsed = parseVariable(variableName);
        const baseKey = getBaseKeyForVariable(variableName);
        const suffix = parsed.alias
          ? ` (${startCase(parsed.alias)})`
          : parsed.instance
            ? ` #${parsed.instance}`
            : "";

        return {
          ...item,
          title: `${item.title}${suffix}`,
          variable: variableName,
          baseVariable: item.variable,
          baseKey,
          isFormattingVariant: variableName !== baseKey,
          displayTitle: `${item.title}${suffix}`,
          alias: parsed.alias,
          instance: parsed.instance,
        };
      });
    }) as TemplateDictionaryFormItem[];

    const allItems: TemplateDictionaryFormItem[] = [
      ...knownItems,
      ...unknownItems,
    ];

    return groupBy(allItems, "parent") as Record<
      string,
      TemplateDictionaryFormItem[]
    >;
  }, [mappableVariables, unknownItems]);

  const buildInteractivePreviewInputs = useCallback(
    () =>
      buildSignatureRequestMergeInputs(
        mappableVariables,
        effectiveMappedVariableValues,
      ),
    [effectiveMappedVariableValues, mappableVariables],
  );

  const getInteractiveVariableConfig = useCallback(
    (variable: string): PreviewVariableConfig => {
      const item = Object.values(templateDictionaryItemsGroupedByParent)
        .flatMap((items) => items)
        .find((candidate) => candidate.variable === variable);

      if (!item) {
        return { label: startCase(variable), options: [] };
      }

      return {
        label: item.displayTitle,
        options: resolveTemplateDictionaryOptionsForItem(
          item,
          templateDictionaryOptions,
          item.parent,
        ),
      };
    },
    [templateDictionaryItemsGroupedByParent, templateDictionaryOptions],
  );

  const handleInteractiveVariableSelect = useCallback(
    (variable: string, value: string) => {
      const storageKey = getBaseKeyForSignatureRequestVariable(variable);
      setMappedVariableValues((previous) => ({
        ...previous,
        [storageKey]: value,
      }));
    },
    [setMappedVariableValues],
  );

  useEffect(() => {
    const updates: Record<string, string> = {};

    Object.values(templateDictionaryItemsGroupedByParent)
      .flatMap((items) => items)
      .forEach((dictionaryItem) => {
        if (dictionaryItem.lockedAtGeneration) {
          return;
        }

        const storageKey = getBaseKeyForVariable(dictionaryItem.variable);
        if (storageKey in effectiveMappedVariableValues) {
          return;
        }

        const contactValue = getAutoAppliedContactTemplateFieldValue(
          autoApplyContacts,
          dictionaryItem.parent,
          normalizeBaseVariable(dictionaryItem.baseVariable).normalized,
        );
        if (contactValue !== undefined) {
          updates[storageKey] = contactValue;
          return;
        }

        const options = resolveTemplateDictionaryOptionsForItem(
          dictionaryItem,
          getTemplateDefaultOptions(
            dictionaryItem.baseVariable,
            templateDictionaryOptions,
            defaultTemplateDictionaryOptions,
          ),
          dictionaryItem.parent,
        );

        if (options.length !== 1) {
          return;
        }

        const defaultValue = options[0]?.value;
        if (!defaultValue) {
          return;
        }

        updates[storageKey] = defaultValue;
      });

    if (Object.keys(updates).length > 0) {
      setMappedVariableValues((prev) => ({ ...prev, ...updates }));
    }
  }, [
    autoApplyContacts,
    effectiveMappedVariableValues,
    setMappedVariableValues,
    templateDictionaryItemsGroupedByParent,
    templateDictionaryOptions,
    defaultTemplateDictionaryOptions,
  ]);

  return (
    <div className="bg-card grid h-full min-h-0 w-full overflow-hidden lg:grid-cols-[minmax(0,1fr)_400px]">
      <div className="min-h-0 min-w-0 bg-slate-50/40">
        <InteractiveSignatureRequestDocxPreview
          matterId={matterId}
          selectedDocument={selectedDocument}
          canEditDocumentFiles={canEditDocumentFiles}
          buildInputs={buildInteractivePreviewInputs}
          getVariableConfig={getInteractiveVariableConfig}
          onVariableSelect={handleInteractiveVariableSelect}
        />
      </div>

      <div className="border-border/80 bg-card flex h-full min-h-0 flex-col border-l">
        <div className="p-4 pb-3">
          <Typography className="text-xl font-semibold">
            Map variables
          </Typography>
          <Typography className="text-muted-foreground mt-2 text-xs leading-relaxed">
            You can add or change signers, including additional signers, on the
            next step.
          </Typography>
          {firmFlowHelperText ? (
            <Typography className="text-muted-foreground mt-2 text-xs leading-relaxed">
              {firmFlowHelperText}
            </Typography>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
          <SignerSlotAssignments {...signerAssignments} matterId={matterId} />
          {signerAssignments.slots.length > 0 &&
          mappableVariables.length > 0 ? (
            <Separator className="my-6" />
          ) : null}
          {mappableVariables.length === 0 &&
          signerAssignments.slots.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
              <Typography weight="semibold">
                No additional variables to map.
              </Typography>
              <Typography className="text-muted-foreground text-sm">
                Continue to the next step to review and send the request.
              </Typography>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.keys(templateDictionaryItemsGroupedByParent).map(
                (key, i, keys) => {
                  const itemsForKey =
                    templateDictionaryItemsGroupedByParent[key];
                  const instanceGroups = buildInstanceGroups(itemsForKey);

                  return (
                    <div key={key} className="space-y-6">
                      {instanceGroups.map((group, idx) => {
                        const groupSuffix = getGroupHeadingSuffix(
                          group,
                          instanceGroups.length,
                        );
                        const sectionLabel =
                          getRoleLabelForKey(key) ?? startCase(key);
                        const headingLabel = `${sectionLabel}${groupSuffix}`;

                        return (
                          <div
                            key={`${key}-${group.key}`}
                            className="space-y-4"
                          >
                            <Typography className="text-base font-semibold">
                              {headingLabel}
                            </Typography>

                            <div className="space-y-4">
                              {getVisibleGroupItems(group.items).map(
                                (dictionaryItem) => {
                                  const storageKey = getBaseKeyForVariable(
                                    dictionaryItem.variable,
                                  );
                                  const fieldValue =
                                    effectiveMappedVariableValues[storageKey] ??
                                    "";
                                  const options =
                                    resolveTemplateDictionaryOptionsForItem(
                                      dictionaryItem,
                                      templateDictionaryOptions,
                                      key,
                                    );
                                  const handleChange = (value: string) => {
                                    setMappedVariableValues((prev) => ({
                                      ...prev,
                                      [storageKey]: value,
                                    }));
                                  };

                                  if (dictionaryItem.lockedAtGeneration) {
                                    if (
                                      dictionaryItem.lockedDisplayAsMultiline
                                    ) {
                                      return (
                                        <div
                                          key={dictionaryItem.variable}
                                          className="space-y-2"
                                        >
                                          <div className="text-sm font-medium">
                                            {dictionaryItem.displayTitle}
                                          </div>
                                          <Textarea
                                            value={fieldValue}
                                            onChange={(event) => {
                                              handleChange(event.target.value);
                                            }}
                                            rows={Math.max(
                                              3,
                                              fieldValue.split(
                                                /<br\s*\/?>|\r?\n/i,
                                              ).length,
                                            )}
                                            placeholder={`Auto-generated ${dictionaryItem.displayTitle}`}
                                            className="min-h-[120px] w-full resize-y whitespace-pre-wrap"
                                          />
                                        </div>
                                      );
                                    }

                                    return (
                                      <div
                                        key={dictionaryItem.variable}
                                        className="space-y-2"
                                      >
                                        <div className="text-sm font-medium">
                                          {dictionaryItem.displayTitle}
                                        </div>
                                        <Input
                                          value={fieldValue}
                                          readOnly
                                          disabled
                                          placeholder={`Auto-generated ${dictionaryItem.displayTitle}`}
                                        />
                                      </div>
                                    );
                                  }

                                  return (
                                    <CreatableSelect
                                      key={dictionaryItem.variable}
                                      label={dictionaryItem.displayTitle}
                                      placeholder={`Select or enter a ${dictionaryItem.displayTitle}`}
                                      value={fieldValue}
                                      onChange={handleChange}
                                      createMessage={`Add ${dictionaryItem.displayTitle}`}
                                      emptyMessage={`No ${dictionaryItem.displayTitle} found. Start typing to create one.`}
                                      options={options}
                                    />
                                  );
                                },
                              )}
                            </div>

                            {idx < instanceGroups.length - 1 && <Separator />}
                          </div>
                        );
                      })}

                      {i < keys.length - 1 && <Separator />}
                    </div>
                  );
                },
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MapVariablesFirmFlowForm(props: MapVariablesStepProps) {
  const { currentUser } = useUser();
  const { data: firmOffices, isLoading: officesLoading } = useQuery(
    getFirmOfficesQueryOptions(),
  );
  const emptyMatter = useMemo(() => getEmptyMatterForFirmTemplateMapping(), []);
  const templateDictionaryOptions = useMemo<TemplateDictionaryOptionsMap>(
    () =>
      buildTemplateDictionaryOptions({
        matter: emptyMatter,
        firm: currentUser.firm,
        firmOffices: firmOffices?.data,
        contacts: [],
        clientInsurance: [],
        defendantInsurance: [],
        clientVehicles: [],
        defendantVehicles: [],
      }),
    [currentUser.firm, emptyMatter, firmOffices?.data],
  );

  const isLoading = officesLoading;
  if (isLoading) {
    return (
      <div className="flex h-full min-h-0 w-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <MapVariablesForm
      matterId={props.matterId}
      selectedDocument={props.selectedDocument}
      selectedDocumentVariables={props.selectedDocumentVariables}
      templateDictionaryOptions={templateDictionaryOptions}
      mappedVariableValues={props.mappedVariableValues}
      setMappedVariableValues={props.setMappedVariableValues}
      canEditDocumentFiles={false}
      signers={props.signers}
      setSigners={props.setSigners}
      firmFlowHelperText="Matter-specific dropdowns are empty here because no matter is loaded. Firm name, offices, and similar fields use your firm profile. For any variable, type a value or use Add to create one."
    />
  );
}

function MapVariablesMatterStep({
  subItem,
  matterId,
  selectedDocument,
  selectedDocumentVariables,
  mappedVariableValues,
  setMappedVariableValues,
  signers,
  setSigners,
}: MapVariablesStepProps) {
  const { currentUser, hasPagePermission } = useUser();
  const formatInsurancePolicyLimits = useFormatInsurancePolicyLimits();
  const queryClient = useQueryClient();
  const { data: matterData, isLoading } = useQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const { data: firmOffices } = useQuery(getFirmOfficesQueryOptions());
  const {
    data: contactsData,
    isLoading: contactsLoading,
    isError: contactsError,
  } = useQuery(
    templateContactsQueryOptions(
      uniq([
        ...(matterData?.data
          ? getMatterContactConnections(matterData.data)
          : []
        ).map((connection) => connection.contactId),
        ...(matterData?.data.insurances ?? []).flatMap((insurance) =>
          insurance.connections.map((connection) => connection.targetObjectId),
        ),
        ...(matterData?.data.vehicles ?? []).flatMap((vehicle) =>
          vehicle.connections.map((connection) => connection.targetObjectId),
        ),
        ...(matterData?.data.incidentReports ?? []).flatMap((report) =>
          report.connections.map((connection) => connection.targetObjectId),
        ),
        ...(matterData?.data
          ? getMedicalTreatmentProviderContactIds(matterData.data)
          : []),
        ...(matterData?.data
          ? getSettlementNegotiationContactIds(matterData.data)
          : []),
      ]),
      queryClient,
      hasPagePermission("contacts.detail"),
    ),
  );
  const { clientVehicles, defendantVehicles } = useVehiclesViewModel(matterId);
  const matter = matterData?.data;
  const contacts = useMemo(() => contactsData ?? [], [contactsData]);

  const contactsWithConnections = useMemo<ContactConnectionWithContact[]>(
    () =>
      matter
        ? [
            ...getMatterContactConnections(matter)
              .filter(
                (connection) =>
                  !connection.incidentReportId &&
                  !isSettlementNegotiationContactConnection(connection) &&
                  connection.label !== "Provider",
              )
              .map((connection) => ({
                ...connection,
                contact:
                  contacts.find(
                    (contact) => contact.id === connection.contactId,
                  ) ?? connection.contact,
              })),
            ...buildIncidentReportSourceConnections(
              matter.incidentReports,
              contacts,
            ),
            ...buildMedicalTreatmentProviderConnections(matter, contacts),
            ...buildSettlementNegotiationContactConnections(matter, contacts),
          ]
        : [],
    [contacts, matter],
  );
  const client = contactsWithConnections.find(
    (connection) => connection.label === "Client",
  );

  const thirdPartyContactIds = useMemo(
    () => (matter ? getThirdPartyMatterContactIds(matter) : new Set<string>()),
    [matter],
  );

  const clientInsurance: InsuranceWithConnections[] = useMemo(
    () =>
      (matter?.insurances ?? [])
        .map((insurance) =>
          buildInsuranceWithConnections(
            insurance,
            contacts,
            formatInsurancePolicyLimits(insurance),
          ),
        )
        .filter((insurance) =>
          [
            ...getInsuranceContactConnections(insurance, "Insured"),
            ...getInsuranceContactConnections(insurance, "Policy Holder"),
          ].some(
            (connection) => connection.targetObjectId === client?.contactId,
          ),
        ),
    [
      client?.contactId,
      contacts,
      formatInsurancePolicyLimits,
      matter?.insurances,
    ],
  );

  const defendantInsurance: InsuranceWithConnections[] = useMemo(
    () =>
      (matter?.insurances ?? [])
        .map((insurance) =>
          buildInsuranceWithConnections(
            insurance,
            contacts,
            formatInsurancePolicyLimits(insurance),
          ),
        )
        .filter((insurance) =>
          [
            ...getInsuranceContactConnections(insurance, "Insured"),
            ...getInsuranceContactConnections(insurance, "Policy Holder"),
          ].some((connection) =>
            thirdPartyContactIds.has(connection.targetObjectId),
          ),
        ),
    [
      contacts,
      formatInsurancePolicyLimits,
      matter?.insurances,
      thirdPartyContactIds,
    ],
  );

  const { templateDictionaryOptions, defaultTemplateDictionaryOptions } =
    useMemo(
      () =>
        matter
          ? buildContextualTemplateDictionaryOptions(
              {
                matter,
                firm: currentUser.firm,
                firmOffices: firmOffices?.data,
                contacts: contactsWithConnections,
                clientInsurance,
                defendantInsurance,
                clientVehicles,
                defendantVehicles,
                vehicleOwnerContacts: contacts,
              },
              subItem,
            )
          : {
              templateDictionaryOptions: {},
              defaultTemplateDictionaryOptions: {},
            },
      [
        clientInsurance,
        clientVehicles,
        contactsWithConnections,
        contacts,
        defendantInsurance,
        defendantVehicles,
        currentUser.firm,
        firmOffices?.data,
        matter,
        subItem,
      ],
    );

  if (contactsError) {
    return (
      <Typography>
        Unable to load template contacts. Please try again.
      </Typography>
    );
  }

  if (isLoading || contactsLoading || !matter) {
    return (
      <div className="flex h-full min-h-0 w-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <MapVariablesForm
      matterId={matterId}
      selectedDocument={selectedDocument}
      selectedDocumentVariables={selectedDocumentVariables}
      templateDictionaryOptions={templateDictionaryOptions}
      defaultTemplateDictionaryOptions={defaultTemplateDictionaryOptions}
      autoApplyContacts={contactsWithConnections}
      mappedVariableValues={mappedVariableValues}
      setMappedVariableValues={setMappedVariableValues}
      canEditDocumentFiles={false}
      signers={signers}
      setSigners={setSigners}
    />
  );
}

export const MapVariablesStep = (props: MapVariablesStepProps) => {
  if (props.selectedDocument?.type === "firm-file") {
    if (props.matterId && !props.isFirmSignatureFlow) {
      return <MapVariablesMatterStep {...props} />;
    }

    return <MapVariablesFirmFlowForm {...props} />;
  }

  if (
    props.isFirmSignatureFlow &&
    props.selectedDocument?.type === "matter-template"
  ) {
    return <MapVariablesFirmFlowForm {...props} />;
  }

  return <MapVariablesMatterStep {...props} />;
};
